// src/hooks/useGoogleSignIn.ts
//
// Thin wrapper around Google Identity Services (GIS — the modern post-2022
// Google Sign-In SDK loaded by the <script src="https://accounts.google.com/gsi/client" />
// tag in index.html).
//
// We deliberately DO NOT install @react-oauth/google or any wrapper package:
// GIS is small, stable, and the wrapper adds no value while shipping a few KB
// of extra bundle. The only ergonomic price we pay is the global `window.google`
// type, declared at the bottom of this file.
//
// Returns:
//   - `configured`: whether VITE_GOOGLE_OAUTH_CLIENT_ID is set in the build.
//                   When false, the Google buttons should stay disabled.
//   - `ready`:      whether the GIS <script> has loaded and we've initialised
//                   our handler. Buttons should disable themselves until this is true.
//   - `signIn()`:   triggers the popup flow and resolves with the ID token,
//                   or rejects with `{ reason: 'cancelled' | 'unavailable' | 'unknown' }`.
//
// Threading model: GIS' callback is invoked once per popup completion. We bridge
// the callback world into a Promise per signIn() call by capturing the resolver
// in a ref and replacing GIS' onSuccess each call. This is safe because GIS
// reuses the same handler until `initialize` is called again.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ?? '').trim();

export type GoogleSignInError =
  | { reason: 'cancelled' }       // user closed the popup
  | { reason: 'unavailable' }     // GIS script didn't load (network/blockers)
  | { reason: 'not_configured' }  // env var missing — caller should keep the button disabled
  | { reason: 'unknown'; detail?: string };

interface PendingResolver {
  resolve: (idToken: string) => void;
  reject: (err: GoogleSignInError) => void;
}

export function useGoogleSignIn() {
  const configured = GOOGLE_CLIENT_ID.length > 0;

  const [ready, setReady] = useState(false);
  const pendingRef = useRef<PendingResolver | null>(null);

  // Poll for window.google.accounts.id to appear. The GIS <script> is loaded
  // async/defer in index.html so we can't assume it's there at mount.
  useEffect(() => {
    if (!configured) return;

    let cancelled = false;
    let timer: number | undefined;

    const tryInit = () => {
      if (cancelled) return;
      const gid = (window as unknown as { google?: GoogleAccountsGlobal }).google?.accounts?.id;
      if (!gid) {
        // Try again shortly. We give up after ~5s — the script either loaded
        // or it's been blocked (uBlock, NoScript, corporate proxy, offline).
        timer = window.setTimeout(tryInit, 200);
        return;
      }

      gid.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: { credential?: string; error?: string }) => {
          const pending = pendingRef.current;
          pendingRef.current = null;
          if (!pending) return; // popup completed without a pending caller — ignore
          if (response.credential) {
            pending.resolve(response.credential);
          } else {
            pending.reject({
              reason: 'unknown',
              detail: response.error ?? 'No credential in GIS response',
            });
          }
        },
        // ux_mode 'popup' keeps us off Google's full-page redirect, which would
        // dump our React state on return. We need that state (verificationId,
        // signupToken etc. on the sign-up wizard) to survive the round-trip.
        ux_mode: 'popup',
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      setReady(true);
    };

    tryInit();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [configured]);

  const signIn = useCallback((): Promise<string> => {
    if (!configured) return Promise.reject({ reason: 'not_configured' } as GoogleSignInError);
    const gid = (window as unknown as { google?: GoogleAccountsGlobal }).google?.accounts?.id;
    if (!gid || !ready) {
      return Promise.reject({ reason: 'unavailable' } as GoogleSignInError);
    }

    // Cancel any in-flight popup before opening a new one.
    if (pendingRef.current) {
      pendingRef.current.reject({ reason: 'cancelled' });
      pendingRef.current = null;
    }

    return new Promise<string>((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      // Prompt is the GIS API for the One Tap / popup flow. The callback we
      // registered in initialize() is what receives the ID token.
      gid.prompt((notification: GooglePromptMomentNotification) => {
        // GIS distinguishes "skipped" (user not signed-in to Google in this
        // browser, popup couldn't open) from "dismissed" (user clicked away).
        // Both are user-cancel from our perspective — keep the UX simple.
        if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
          const pending = pendingRef.current;
          pendingRef.current = null;
          if (pending) {
            pending.reject({
              reason: 'unavailable',
              detail: notification.getNotDisplayedReason?.() ?? notification.getSkippedReason?.(),
            });
          }
        }
      });
    });
  }, [configured, ready]);

  return useMemo(
    () => ({ configured, ready, signIn }),
    [configured, ready, signIn],
  );
}

// ---------------------------------------------------------------------------
// Minimal types for the bits of GIS we actually use. The full @types package
// (@types/google.accounts) is huge and updated infrequently — this is enough.
// ---------------------------------------------------------------------------

interface GooglePromptMomentNotification {
  isNotDisplayed?: () => boolean;
  isSkippedMoment?: () => boolean;
  getNotDisplayedReason?: () => string | undefined;
  getSkippedReason?: () => string | undefined;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: { credential?: string; error?: string }) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  prompt: (momentListener?: (notification: GooglePromptMomentNotification) => void) => void;
}

interface GoogleAccountsGlobal {
  accounts: {
    id: GoogleAccountsId;
  };
}
